import { fabric } from 'fabric';
import checkmark from '../../../assets/images/checkbox-red.svg';
import { AnnotationsForGrid } from '../Annotation/annotation.types';
import { getTopLeftOfImageBoundingBox } from '../StitchedView/Hooks/Helpers';
import { AnnotatedFinding } from './prepareShapesSelector';

const indicatorName = 'selected_image_indicator';
export const indicatorColor = 'darkred';
export const indicatorColorLight = '#1f3a93';
export const mlProposalIndicatorColor = 'yellow';
export const notVisibleIndicatorColor = 'green';
export const dirtyIndicatorColor = 'orangered';

export const indicatorTransparentColor = 'rgba(139,0,0,0.5)';

const min = 99;
const max = 999999;
export const strokeWidthThin = 1;
export const strokeWidthThick = 5;
export const initialZoomLevel = 1;

export const mapRawToFabricImages = ({ rawImages, isStitched, stitchingManipulationRules, canvasHeight, canvasWidth }: any): any => {
  if (rawImages.length === 0) {
    return {
      fabricImages: [],
      topScale: 1,
      leftScale: 1,
      centringLeftScale: 1,
      zoom: 1
    };
  }

  const newImages = rawImages.filter((x: any) => !x.isHidden);

  // const topScale = Math.min(...newImages.map((i: any) => i.top));
  // const leftScale = Math.min(...newImages.map((i: any) => i.left));

  const images = mapAndScaleImages(newImages, 0, 0, canvasWidth);
  const zoom = calculateZoom(images, canvasHeight);

  // const centringLeftScale = (canvasWidth * zoom) / 2;

  const fabricImages = images.map(imageObj => {
    return {
      left: imageObj.left,
      src: imageObj.src,
      top: imageObj.top,
      angle: imageObj.angle,
      name: imageObj.name,
      scaleX: imageObj.scaleX,
      scaleY: imageObj.scaleY,
      width: imageObj.width,
      height: imageObj.height,
      originX: 'left',
      originY: 'top',
      centeredRotation: true,
      lockUniScaling: true,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      data: imageObj.name,
      visible: true,
      ...stitchingManipulationRules
    };
  });

  return { fabricImages, zoom };
};

const ratio = 1;
const mapAndScaleImages = (images: any[], leftScale: number, topScale: number, canvasWidth: number) => {
  return images.map(image => {
    let scale = canvasWidth / image.width;
    return {
      left: 0,
      top: 0,
      width: image.width * ratio,
      height: image.height * ratio,
      angle: 0,
      src: image.src,
      scaleX: scale,
      scaleY: scale,
      name: image.name,
      visible: !image.isHidden
    };
  });
};

const calculateZoom = (images: any[], canvasHeight: number) => {
  if (images.length === 0) return 1;

  const maxTopPostion = Math.max(...images.map(i => i.top)) + images[images.length - 1].height; // + 50;

  const zoom = maxTopPostion / canvasHeight;

  return zoom;
};

export const getStrokeColor = (finding: AnnotatedFinding) => {
  if (finding.visible === false && !(finding.isReviewed || finding.isQaReviewed)) {
    return notVisibleIndicatorColor;
  }

  if (finding.isMlProposal && !(finding.isReviewed || finding.isQaReviewed)) {
    return mlProposalIndicatorColor;
  }

  return indicatorColorLight;
};

export const getStrokeColorForAnnotation = (finding: AnnotationsForGrid) => {
  if (finding.visible === false && !(finding.isReviewed || finding.isQaReviewed)) {
    return notVisibleIndicatorColor;
  }

  if (finding.isMlProposal && !(finding.isReviewed || finding.isQaReviewed)) {
    return mlProposalIndicatorColor;
  }

  return indicatorColorLight;
};

//severity logic
// export const getStrokeColor = (finding: AnnotatedFinding) => {
//   let color = indicatorColorLight;

//   if (finding.severity !== undefined) {
//     color = getSeverityColor(finding.severity);
//   }

//   return color;
// };

// export const getStrokeColorForAnnotation = (finding: AnnotationsForGrid) => {
//   let color = indicatorColorLight;

//   const severity = getSeverityPropValue(finding);

//   if (severity !== undefined) {
//     color = getSeverityColor(severity);
//   }

//   return color;
// };

const TARGET_FIND_TOLERANCE = 4;
const CORNER_SIZE = 6;
const OPACITY = 1;
const FINDING_FILL = 'transparent';
const CORNER_COLOR = 'yellow';

export const fabricObjectProperties = {
  stroke: indicatorColorLight,
  strokeWidth: strokeWidthThin,
  fill: FINDING_FILL,
  opacity: OPACITY,
  selectable: true,
  hasBorders: false,
  hasControls: true,
  perPixelTargetFind: true,
  targetFindTolerance: TARGET_FIND_TOLERANCE,
  evented: true,
  borderColor: indicatorColor,
  cornerColor: CORNER_COLOR,
  cornerSize: CORNER_SIZE,
  transparentCorners: false,
  strokeUniform: true,
  lockMovementX: false,
  lockMovementY: false,
  lockScalingX: false,
  lockScalingY: false,
  lockUniScaling: false,
  lockRotation: false
};

export const lockedFabricObjectProperties = {
  lockMovementX: true,
  lockMovementY: true,
  lockScalingX: true,
  lockScalingY: true,
  lockUniScaling: true,
  lockRotation: true
};

export const setSelectedImageProperties = (object: fabric.Object, canvas: fabric.Canvas) => {
  object.stroke = indicatorColor;
  object.strokeWidth = strokeWidthThick;
  object.borderColor = 'transparent';

  if (!object) return;

  const indicatorSide = object.width! * 0.15;
  const [xmin, ymin] = getTopLeftOfImageBoundingBox(object as fabric.Image);
  const selectorX = xmin - indicatorSide * 0.3;
  const selectorY = ymin - indicatorSide * 0.3;

  fabric.Image.fromURL(checkmark, function (oImg) {
    oImg.set({
      name: indicatorName,
      left: selectorX,
      top: selectorY,
      width: indicatorSide,
      height: indicatorSide,

      clipTo: (ctx: any) => {
        ctx.arc(20, 0, 150, 0, Math.PI * 2, true);
      }
    });
    oImg.evented = false;
    oImg.selectable = false;
    canvas.add(oImg);
  });
};

export const setUnselectedImageProperties = (object: fabric.Object, canvas: fabric.Canvas) => {
  object.stroke = 'transparent';

  const indicator = canvas.getObjects().find(x => x.name === indicatorName);
  if (indicator) {
    canvas.remove(indicator);
  }
};

export const setSelectedFindingProperties = (object: fabric.Object) => {
  object.hasBorders = true;
  // object.borderColor = indicatorColor;
  // object.stroke = indicatorColor;
  object.strokeWidth = strokeWidthThin;
};

export const getCurrentZoom = (canvas: fabric.Canvas, event?: WheelEvent) => {
  const delta = event && event.deltaY > 0 ? 150 : -150;
  let zoom = canvas.getZoom();
  zoom = zoom - delta / 1000;
  if (zoom > 10) zoom = 10;
  if (zoom < 0.01) zoom = 0.01;

  return zoom;
};

export function getUniqueId(): string {
  const random = Math.floor(Math.random() * (max - min + 1)) + min;
  const id = new Date().getTime() + random;
  return id.toString();
}

export function centerToObject(canvasEl: fabric.Canvas, selectedObject: fabric.Object) {
  canvasEl.setZoom(1);
  canvasEl.renderAll();
  let zoom = 1;
  const padding = 50;
  const { height, width, top, left } = selectedObject;

  if (!height || !width || !top || !left) {
    return;
  }

  if (height >= width) {
    zoom = canvasEl.getHeight() / (height + padding);
  } else {
    zoom = canvasEl.getWidth() / (width + padding);
  }

  const vpw = canvasEl.getWidth() / zoom;
  const vph = canvasEl.getHeight() / zoom;
  const x = left - vpw / 2 + width / 2;
  const y = top - vph / 2 + height / 2;
  const point = new fabric.Point(x, y);
  canvasEl.absolutePan(point);
  canvasEl.setZoom(zoom);

  adjustStrokeToZoom(canvasEl, zoom);
}

export function shouldShowFinding(showAll: boolean, showML: boolean, isReviewed: boolean) {
  return (showAll && showML) || (showAll && !showML && isReviewed) || (!showAll && showML && !isReviewed);
}

export function adjustStrokeToZoom(canvasEl: fabric.Canvas, zoom: number) {
  canvasEl.getObjects().forEach((obj: any) => {
    if (obj.type === 'circle') {
      obj.radius = 5 / zoom;
    }

    if (obj.type === 'line') {
      obj.strokeWidth = 2 / zoom;
    }

    if (obj.type === 'rect' || obj.type === 'polygon') {
      obj.strokeWidth = strokeWidthThin / zoom;
    }

    if (obj.data?.type === 'image-edge') {
      obj.strokeWidth = 30 / zoom;
    }
  });
}
